import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, Loader2 } from 'lucide-react';

export default function RegisterPage({ embutido = false, aoIrParaLogin = null }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('athlete');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await register(name, email, password, role);
      // Quem se cadastra como FEDERAÇÃO vai direto escolher/criar a federação
      // dele (Fluxo D): o cadastro cria a conta, o vínculo vem em seguida.
      navigate(role === 'federation' ? '/federacoes/vincular' : '/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* ETAPA VISUAL 7 — mesma correção do login: `flex-col` + `justify-center`
     evitam o corte do topo quando o formulário passa da janela, e o padding
     vertical subiu de 16px (`p-4`) para 32/40px. Como o cadastro tem mais
     campos, aqui a página rola — mas agora rola mantendo o respiro nas pontas,
     em vez de cortar o logo em cima. */
  /* ETAPA 11 — `embutido`: mesma tela sem a moldura de página, para entrar na
     landing ao lado do hero. A lógica (estado, API, Google, erro) é a mesma. */
  return (
    <div className={embutido ? '' : 'min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-4 py-6 sm:py-8'}>
      <div className={embutido ? '' : 'w-full max-w-md'}>
        {!embutido && (
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8860B] mb-2">
              <Trophy className="w-6 h-6 text-black" />
            </div>
            <h1 className="text-2xl font-bold text-white font-['Chivo']">BJJ Manager</h1>
          </div>
        )}

        {/* Register Form */}
        <div className="bg-[#141414] border border-[#27272A] rounded-xl p-5">
          <h2 className="text-lg font-bold text-white mb-3 font-['Chivo']">Cadastro</h2>
          
          {error && (
            <div className="mb-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#A1A1AA]">Nome completo</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                required
                data-testid="register-name"
                className="bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#71717A] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A1A1AA]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                data-testid="register-email"
                className="bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#71717A] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A1A1AA]">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="register-password"
                className="bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#71717A] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-[#A1A1AA]">Tipo de conta</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger 
                  data-testid="register-role"
                  className="bg-[#1A1A1A] border-[#27272A] text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                >
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  <SelectItem value="athlete" className="text-white hover:bg-[#27272A]">Atleta</SelectItem>
                  <SelectItem value="professor" className="text-white hover:bg-[#27272A]">Professor</SelectItem>
                  <SelectItem value="team" className="text-white hover:bg-[#27272A]">Equipe</SelectItem>
                  <SelectItem value="federation" className="text-white hover:bg-[#27272A]">Federação</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="register-submit"
              className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold py-3"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Criar conta'
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <p className="text-[#71717A]">
              Já tem conta?{' '}
              {aoIrParaLogin ? (
                <button type="button" onClick={aoIrParaLogin} className="text-[#D4AF37] hover:text-[#E5C158]" data-testid="login-link">
                  Entrar
                </button>
              ) : (
                <Link to="/login" className="text-[#D4AF37] hover:text-[#E5C158]" data-testid="login-link">
                  Entrar
                </Link>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
